import { catchError, from, map, Observable, of, switchMap } from 'rxjs'

import { Injectable } from '@angular/core'
import { Localization } from '@aa-app-localization'

import { Product } from '@app-domains/content-blocks/components/product-card/product-card.types'
import { ProductFilterInput, ProductSearchHit } from '@app-domains/meilisearch/types/product-search.types'
import { ProductSearchService } from '@app-domains/meilisearch/services/product-search.service'
import { mapSearchHitToProductHit } from '@app-domains/meilisearch/mappers/product-hit.mapper'
import { brandFilter, productTypeFilter, rangeFilter } from '@app-domains/meilisearch/lib/product-filter.lib'
import { ProductService } from '@app-services/product/product.service'

export enum RelatedProductsStrategy {
    vector = 'vector',
    mixed = 'mixed',
    attribute = 'attribute',
    none = 'none',
}

export type RelatedProductsResult = {
    products: Product[]
    strategy: RelatedProductsStrategy
}

const MIN_RELATED = 3
const MAX_SEED_LENGTH = 500

@Injectable({ providedIn: 'root' })
export class RelatedProductsService {

    constructor(
        private readonly productSearchService: ProductSearchService,
        private readonly productService: ProductService,
    ) {
    }

    public getRelatedProducts(
        product: Product,
        locale: Localization.Locale,
        limit: number,
    ): Observable<RelatedProductsResult> {
        return from(this.fetchRelatedProducts(product, locale, limit)).pipe(
            catchError(() => of({ products: [], strategy: RelatedProductsStrategy.none })),
        )
    }

    public getSameSeriesProducts(
        product: Product,
        locale: Localization.Locale,
        limit: number,
    ): Observable<Product[]> {
        const range = this.resolveRange(product)

        if (! range) {
            return of([])
        }

        return from(this.fetchFilteredProducts(
            product,
            locale,
            limit,
            [productTypeFilter(), rangeFilter(range)],
        )).pipe(
            catchError(() => of([])),
        )
    }

    public getSameBrandProducts(
        product: Product,
        locale: Localization.Locale,
        limit: number,
    ): Observable<Product[]> {
        if (! product.brand) {
            return of([])
        }

        return from(this.fetchFilteredProducts(
            product,
            locale,
            limit,
            [productTypeFilter(), brandFilter(product.brand)],
        )).pipe(
            catchError(() => of([])),
        )
    }

    public getLastViewedProducts(
        eans: string[],
        currentEan: string,
        locale: Localization.Locale,
        limit: number,
    ): Observable<Product[]> {
        const orderedEans = eans
            .filter((ean) => ean !== currentEan)
            .slice(0, limit)

        if (orderedEans.length === 0) {
            return of([])
        }

        return this.productService.getSimpleProducts({ eans: orderedEans }).pipe(
            map((result) => {
                if (result.error || ! result.data?.productsByEan) {
                    return []
                }

                const products = this.productService.transformSimpleToProduct(
                    result.data.productsByEan,
                    locale,
                )

                return this.orderProductsByEans(products, orderedEans)
            }),
            switchMap((products) => this.productService.enhance(products)),
            catchError(() => of([])),
        )
    }

    public buildSeedQuery(product: Product): string {
        const parts = [
            product.name,
            product.brand,
            this.resolveRange(product),
            ...(product.categoryNames ?? []),
            ...product.materials,
            ...product.subMaterials,
            ...(product.colors?.map((color) => color.name) ?? []),
            product.description,
        ].filter(Boolean) as string[]

        const unique = [...new Set(parts)]

        return unique.join(' ').slice(0, MAX_SEED_LENGTH)
    }

    private async fetchFilteredProducts(
        product: Product,
        locale: Localization.Locale,
        limit: number,
        filters: ProductFilterInput[],
    ): Promise<Product[]> {
        const result = await this.productSearchService.execute({
            query: '',
            locale,
            page: 1,
            perPage: limit + 1,
            semanticRatio: 0,
            filters,
            sort: [],
        })

        const hits = this.excludeSelf(result.hits, product.ean).slice(0, limit)

        if (hits.length === 0) {
            return []
        }

        return this.toProducts(hits)
    }

    private async fetchRelatedProducts(
        product: Product,
        locale: Localization.Locale,
        limit: number,
    ): Promise<RelatedProductsResult> {
        if (! product.ean) {
            return { products: [], strategy: RelatedProductsStrategy.none }
        }

        try {
            const recommendationHits = this.excludeSelf(
                await this.productSearchService.getRecommendations(product.ean, locale, limit + 1),
                product.ean,
            )

            if (recommendationHits.length >= MIN_RELATED) {
                const products = this.toProducts(recommendationHits.slice(0, limit))

                return {
                    products,
                    strategy: RelatedProductsStrategy.vector,
                }
            }

            const toppedUp = await this.topUpWithAttributeFallback(
                product,
                locale,
                limit,
                recommendationHits,
            )

            if (toppedUp.length >= MIN_RELATED) {
                const products = this.toProducts(toppedUp.slice(0, limit))

                return {
                    products,
                    strategy: recommendationHits.length > 0
                        ? RelatedProductsStrategy.mixed
                        : RelatedProductsStrategy.attribute,
                }
            }
        } catch {
            // recommendations endpoint failed; use attribute-only results below
        }

        const attributeHits = await this.fetchAttributeOnly(product, locale, limit)

        if (attributeHits.length >= MIN_RELATED) {
            const products = this.toProducts(attributeHits.slice(0, limit))

            return {
                products,
                strategy: RelatedProductsStrategy.attribute,
            }
        }

        return { products: [], strategy: RelatedProductsStrategy.none }
    }

    private async topUpWithAttributeFallback(
        product: Product,
        locale: Localization.Locale,
        limit: number,
        existingHits: ProductSearchHit[],
    ): Promise<ProductSearchHit[]> {
        const excludeEans = new Set([
            product.ean,
            ...existingHits.map((hit) => hit.ean),
        ])
        const additional = await this.fetchAttributeOnly(
            product,
            locale,
            limit,
            excludeEans,
        )

        return this.deduplicateHits([...existingHits, ...additional])
    }

    private async fetchAttributeOnly(
        product: Product,
        locale: Localization.Locale,
        limit: number,
        excludeEans: Set<string> = new Set([product.ean]),
    ): Promise<ProductSearchHit[]> {
        const results: ProductSearchHit[] = []
        const range = this.resolveRange(product)

        if (range) {
            const rangeResult = await this.productSearchService.execute({
                query: '',
                locale,
                page: 1,
                perPage: limit + excludeEans.size,
                semanticRatio: 0,
                filters: [productTypeFilter(), rangeFilter(range)],
                sort: [],
            })

            results.push(...this.excludeEans(rangeResult.hits, excludeEans))
        }

        if (results.length < limit && product.brand) {
            const brandResult = await this.productSearchService.execute({
                query: '',
                locale,
                page: 1,
                perPage: limit + excludeEans.size + results.length,
                semanticRatio: 0,
                filters: [productTypeFilter(), brandFilter(product.brand)],
                sort: [],
            })

            const seenEans = new Set([
                ...excludeEans,
                ...results.map((hit) => hit.ean),
            ])

            results.push(...this.excludeEans(brandResult.hits, seenEans))
        }

        return this.deduplicateHits(results).slice(0, limit)
    }

    private resolveRange(product: Product): string | undefined {
        return product.range ?? product.collectionName ?? undefined
    }

    private orderProductsByEans(products: Product[], eans: string[]): Product[] {
        const byEan = new Map(products.map((product) => [product.ean, product]))

        return eans
            .map((ean) => byEan.get(ean))
            .filter((product): product is Product => !! product)
    }

    private excludeSelf(hits: ProductSearchHit[], ean: string): ProductSearchHit[] {
        return hits.filter((hit) => hit.ean !== ean)
    }

    private excludeEans(hits: ProductSearchHit[], excludeEans: Set<string>): ProductSearchHit[] {
        return hits.filter((hit) => ! excludeEans.has(hit.ean))
    }

    private deduplicateHits(hits: ProductSearchHit[]): ProductSearchHit[] {
        const seen = new Set<string>()

        return hits.filter((hit) => {
            if (seen.has(hit.ean)) {
                return false
            }

            seen.add(hit.ean)

            return true
        })
    }

    private toProducts(hits: ProductSearchHit[]): Product[] {
        if (hits.length === 0) {
            return []
        }

        const searchHits = hits.map(mapSearchHitToProductHit)

        return this.productService.transformHitToProduct(searchHits)
    }

}
